// The Eng Tour Creator's rules about rooms — the pure half, so
// src/engtour.rooms.test.js can pin them without mounting a 3,000-line
// component. The creator imports these; nothing else should need to.
//
// Three rules live here: which numbered polygons are ROOMS (and not a
// stairwell, an elevator or the building outline that the files number the
// same way); that a stop's label is its room CODE and its title the room's
// NAME, including the one-time move of a name typed into the label; and how a
// typed query ranks the rooms it could mean, by code or by name.

import { roomCodeFor } from './engtourRoomNames.js';

// A room a stop can be assigned to. The building files number stairwells
// ("253st"), elevators ("203el") and the outline itself ("outline") exactly as
// they number rooms, so without this the room-number overlay printed a tag on
// every staircase and the dropdowns offered "E2-240el" as somewhere to send a
// visitor. The type is the primary test; the suffix catches a file whose type
// is missing.
export const NON_ROOM_TYPES = new Set(["outline", "stair", "elevator", "elevatorstairs", "blocked", "corridor"]);
export function isStopRoom(type, roomNumber) {
  if (roomNumber == null || String(roomNumber).trim() === "") return false;
  if (NON_ROOM_TYPES.has(String(type || "").toLowerCase())) return false;
  return !/(st|el)$|^outline$/i.test(String(roomNumber).trim());
}

// ── Label = room code, title = room name ─────────────────────────────────────
// A stop's `label` is the code the tour NAVIGATES by ("E3-362") and is derived
// from building+room, never typed; its `title` is the name visitors read
// ("Engineering Library"). The label used to be free text, so some saved
// stops carried the name in the label and nothing in the title — the viewer
// printed the label as a fallback and it looked right, until the editor needed
// to know which room the stop actually was. This moves such a label into the
// blank title and sets the label to the code. A hand-placed spot (nodeId, no
// room) keeps its own label, which is its name. Mutates in place, like
// migrateFreeRoamToBank; the result is persisted on the next save.
// A room that reads like one: "362", "115A". Some saved stops carry a NAME in
// the room field ("Engineering Library") — those can never resolve to a door
// and are left exactly as they are for the editor to flag and reassign, rather
// than being rewritten by a rule that would have to guess the room.
export const looksLikeRoom = (r) => /^\d+[A-Za-z]?$/.test(String(r == null ? "" : r).trim());
export function normalizeStopLabel(s) {
  if (!s || s.nodeId || !looksLikeRoom(s.room)) return s;
  const code = roomCodeFor(s.building, s.room);
  const label = String(s.label || "").trim();
  if (label === code) return s;
  if (label && !String(s.title || "").trim() && label.toUpperCase() !== code.toUpperCase()) s.title = label;
  s.label = code;
  return s;
}
export function normalizeStopLabels(data) {
  if (!data) return data;
  (data.tours || []).forEach(t => (t.stops || []).forEach(normalizeStopLabel));
  (data.stopBank || []).forEach(normalizeStopLabel);
  return data;
}

// Rank the rooms a typed query could mean, by code ("e3 362", "e3362") or by
// name ("library", "access program"). Codes are compared ignoring spaces and
// dashes; every word of the query must appear in the name. A code match
// outranks a name match, and a prefix outranks a substring.
export function filterRoomChoices(choices, query, limit = 8) {
  const norm = s => String(s || "").toUpperCase().replace(/[\s-]/g, "");
  const nq = norm(query);
  if (!nq) return [];
  const words = String(query).toLowerCase().split(/\s+/).filter(Boolean);
  const scored = [];
  for (const o of choices || []) {
    const code = norm(o.code);
    const codeHit = code.includes(nq);
    const name = String(o.name || "").toLowerCase();
    const nameHit = !!name && words.every(w => name.includes(w));
    if (!codeHit && !nameHit) continue;
    const score = (code.startsWith(nq) ? 4 : codeHit ? 2 : 0) + (nameHit ? (name.startsWith(words[0]) ? 2 : 1) : 0);
    scored.push({ o, score });
  }
  scored.sort((a, b) => b.score - a.score || String(a.o.code).localeCompare(String(b.o.code), undefined, { numeric: true }));
  return scored.slice(0, limit).map(x => x.o);
}
